'use client';

import React, { type ReactNode, useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { Brand } from '@/domainLogic/index.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import Popover, { type PopoverProps, type PopoverTrigger } from '@/app/common/components/Popover';
import type { OpenxDialogProps } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';

type ModalProps = Omit<OpenxDialogProps, 'brand' | 'isMobile' | 'onClose' | 'isOpen' | 'children' | 'footer'>;
type PopoverOnlyProps = Omit<PopoverProps, 'anchorEl' | 'onClose' | 'children' | 'aria-label'>;

export type PopoverOrModalProps = {
  anchorEl: HTMLElement | null;
  isOpen?: boolean;
  onClose?: () => void;
  isMobile: boolean;
  children: ReactNode;
  'aria-label'?: string;
  title?: ReactNode;
  showTitleInPopover?: boolean;
  evo?: boolean;
  footer?: ReactNode;
  forceModal?: boolean;
} & ModalProps &
  PopoverOnlyProps;

const useUncontrolledOpen = (
  anchorEl: HTMLElement | null,
  trigger: PopoverTrigger | undefined,
  controlledOpen: boolean | undefined
) => {
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const justClosedRef = useRef(false);
  const timerRef: RefObject<ReturnType<typeof setTimeout> | undefined> = useRef(undefined);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
    },
    [isControlled]
  );

  const handleOpen = useCallback(() => {
    if (justClosedRef.current) return;
    setOpen(true);
  }, [setOpen]);

  const handleToggle = useCallback(() => {
    if (isControlled || justClosedRef.current) return;
    setInternalOpen((prev) => !prev);
  }, [isControlled]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleDismiss = useCallback(() => {
    justClosedRef.current = true;
    clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      justClosedRef.current = false;
    }, 350);
    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    if (!anchorEl || isControlled) return undefined;
    const listeners: (() => void)[] = [];
    const effectiveTrigger = trigger ?? 'click';

    if (effectiveTrigger === 'click' || effectiveTrigger === 'both') {
      const onClick = () => handleToggle();
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      };
      anchorEl.addEventListener('click', onClick);
      anchorEl.addEventListener('keydown', onKeyDown);
      listeners.push(
        () => anchorEl.removeEventListener('click', onClick),
        () => anchorEl.removeEventListener('keydown', onKeyDown)
      );
    }

    if (effectiveTrigger === 'hover' || effectiveTrigger === 'both') {
      const onPointerEnter = (e: PointerEvent) => {
        if (e.pointerType === 'touch') return;
        handleOpen();
      };
      anchorEl.addEventListener('pointerenter', onPointerEnter);
      listeners.push(() => anchorEl.removeEventListener('pointerenter', onPointerEnter));
    }

    return () => listeners.forEach((fn) => fn());
  }, [anchorEl, trigger, isControlled, handleToggle, handleOpen]);

  return { open, handleClose, handleDismiss };
};

const PopoverOrModal = ({
  anchorEl,
  isOpen: controlledOpen,
  onClose: externalOnClose,
  isMobile,
  children,
  'aria-label': ariaLabel,
  placement,
  align,
  padding,
  borderRadius,
  maxWidth,
  maxHeight,
  width,
  boxShadow,
  trigger,
  closeOnScroll,
  usePortal,
  title,
  showTitleInPopover = false,
  evo,
  footer,
  forceModal = false,
  id,
  className,
  bodyBackgroundColor = 'white',
  disableBrowserHistoryNavigation = true,
  fullScreen = false,
  bodyPadding,
  bodyPaddingS,
  bodyPaddingXS,
  size = MODAL_SIZES.M,
  ...modalProps
}: PopoverOrModalProps) => {
  const { staticData } = usePageWrapperData();
  const brand = staticData.domainValues?.brand as Brand;
  const { isEvoLayout } = useCommonClient();
  const isEvo = evo ?? isEvoLayout;

  const {
    open,
    handleClose: internalClose,
    handleDismiss: internalDismiss,
  } = useUncontrolledOpen(anchorEl, trigger, controlledOpen);

  const handleClose = useCallback(() => {
    internalClose();
    externalOnClose?.();
  }, [internalClose, externalOnClose]);

  const handleDismiss = useCallback(() => {
    internalDismiss();
    externalOnClose?.();
  }, [internalDismiss, externalOnClose]);

  if (isMobile || forceModal) {
    return (
      <OpenXModal
        bodyPadding={bodyPadding}
        bodyPaddingS={bodyPaddingS}
        bodyPaddingXS={bodyPaddingXS}
        isOpen={open}
        isMobile={isMobile}
        brand={brand}
        onClose={handleDismiss}
        disableBrowserHistoryNavigation={disableBrowserHistoryNavigation}
        fullScreen={fullScreen}
        className={className}
        id={id}
        aria-label={ariaLabel}
        bodyBackgroundColor={bodyBackgroundColor}
        scrollShadows={isEvo}
        size={size}
        {...modalProps}
        {...(title !== undefined && {
          header: {
            sticky: true,
            element: (
              <ModalHeader handleClose={handleDismiss} evo={isEvo}>
                {title}
              </ModalHeader>
            ),
          },
        })}
      >
        {children}
        {footer}
      </OpenXModal>
    );
  }

  return open ? (
    <Popover
      id={id}
      anchorEl={anchorEl}
      onClose={handleClose}
      placement={placement}
      align={align}
      padding={padding}
      borderRadius={borderRadius}
      className={className}
      width={width}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      boxShadow={boxShadow}
      trigger={trigger}
      closeOnScroll={closeOnScroll}
      usePortal={usePortal}
      {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
      title={showTitleInPopover && isEvo ? title : undefined}
      footer={footer}
    >
      {children}
    </Popover>
  ) : null;
};

export default PopoverOrModal;
